import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useParams } from 'react-router-dom';

export default function StatsSidebar({ featuredTokens }) {
  const { chain } = useParams();
  const [isVisible, setIsVisible] = useState(false);
  
  // Check if mobile on initial render
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const [isExpanded, setIsExpanded] = useState(!isMobile); // Closed on mobile, open on desktop
  const tokensWithRevenue = (featuredTokens || []).filter(t => Number(t?.yearly_revenue) > 0);
  //total yearly unused variable
  const totalYearlyRevenue = tokensWithRevenue.reduce(
    (sum, t) => sum + Number(t?.yearly_revenue || 0),
    0
  );
  //console.log(totalYearlyRevenue)
  
  // Show sidebar after a short delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100); // Further reduced for immediate responsiveness
    return () => clearTimeout(timer);
  }, []);

  function formatNumber(number) {
    const parsedNumber = parseFloat(number);

    if (isNaN(parsedNumber)) return "0.0";

    // Convert to string without formatting
    const plainStr = parsedNumber.toString().replace('.', '').replace('-', '');

    // If more than 10 digits, return in exponential notation
    if (plainStr.length > 17) {
      return parsedNumber.toExponential(2);
    }

    // Otherwise, return formatted normally
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(parsedNumber);
  }

  return (
    <>
      {/* Toggle Button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`fixed right-4 top-20 z-50 bg-[#1A1A1A] hover:bg-[#2A2A2A] border border-gray-700/50 hover:border-gray-600/70 rounded-full p-3 transition-all duration-300 hover:scale-105 shadow-lg backdrop-blur-sm shadow-[inset_0_2px_4px_rgba(0,0,0,0.6)] ${
          isExpanded ? 'bg-gray-800/30 border-gray-600/40' : 'bg-gray-800/30 border-gray-600/40'
        }`}
        title={isExpanded ? "Hide Stats" : "Show Stats"}
      >
        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          {isExpanded ? (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          )}
        </svg>
      </button>

      {/* Sidebar */}
      <div 
        className={`fixed right-4 top-20 bottom-4 w-80 bg-[#0F0F0F] backdrop-blur-xl border border-gray-700/40 rounded-2xl shadow-2xl z-40 transition-all duration-400 ease-[cubic-bezier(0.4,0,0.2,1)] ${
          isVisible && isExpanded 
            ? 'translate-x-0 opacity-100 pointer-events-auto' 
            : 'translate-x-full opacity-0 pointer-events-none'
        }`}
        style={{
          transform: isVisible && isExpanded ? 'translateX(0)' : 'translateX(100%)',
          willChange: 'transform, opacity',
          backfaceVisibility: 'hidden',
          perspective: '1000px'
        }}
      >
        {/* Header */}
        <div className="p-5 border-b border-gray-700/40">
          <div className="flex items-center justify-between">
            <div className="text-left">
              <h3 className="text-gray-200 font-semibold text-lg mb-1">Token Statistics</h3>
              <p className="text-gray-500 text-sm">Yearly revenue and swap data</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-gradient-to-r from-[#faffbfb5] via-[#c3c17e] to-[#faffbfb5] rounded-full animate-pulse"></div>
              <span className="text-gray-600 text-xs font-medium"></span>
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="h-full overflow-y-auto custom-scrollbar">
          <div className="p-4 space-y-3">
            {tokensWithRevenue.slice(0, 20).map((token, index) => (
              <Link
                key={token.address}
                to={`/${chain}/token/${token.address}`}
                className="block group"
              >
                <div className="bg-[#0A0A0A] hover:bg-[#1A1A1A] border border-gray-700/40 hover:border-gray-600/60 rounded-xl p-4 transition-all duration-300 hover:scale-[1.02] hover:shadow-lg shadow-[inset_0_4px_8px_rgba(0,0,0,0.6)]">
                  {/* Token Header */}
                  <div className="flex items-center gap-3 mb-3">
                    <img 
                      src={token.logo} 
                      alt={token.name} 
                      className="w-8 h-8 rounded-full opacity-90 group-hover:opacity-100 transition-opacity shadow-sm"
                    />
                    <div className="flex-1">
                      <h4 className="text-gray-200 font-semibold text-sm">{token.symbol}</h4>
                      <p className="text-gray-600 text-xs">{token.address.slice(0, 6)}...{token.address.slice(-4)}</p>
                    </div>
                    <div className="text-right">
                      <span className="text-gray-400 text-xs font-medium bg-gray-800/60 px-2 py-1 rounded-full border border-gray-700/50 shadow-[inset_0_1px_2px_rgba(0,0,0,0.6)]">#{index + 1}</span>
                    </div>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gradient-to-r from-[#faffbfb5] via-[#c3c17e] to-[#faffbfb5] rounded-lg p-3 border border-yellow-300/30 shadow-[inset_0_2px_4px_rgba(0,0,0,0.6)]">
                      <div className="text-gray-700 text-xs mb-1 font-medium">Revenue/Year</div>
                      <div className="text-gray-800 font-semibold text-sm">
                        ${formatNumber(Number(token.yearly_revenue))}
                      </div>
                    </div>
                    <div className="bg-gradient-to-r from-[#faffbfb5] via-[#c3c17e] to-[#faffbfb5] rounded-lg p-3 border border-yellow-300/30 shadow-[inset_0_2px_4px_rgba(0,0,0,0.6)]">
                      <div className="text-gray-700 text-xs mb-1 font-medium">Swaps</div>
                      <div className="text-gray-800 font-semibold text-sm">
                        {formatNumber(Number(token.total_swaps))}
                      </div>
                    </div>
                  </div>

                  {/* Additional Info */}
                  <div className="mt-3 pt-3 border-t border-gray-700/40">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">Liquidity</span>
                      <span className="text-gray-400 font-medium">${formatNumber(Number(token.liquidity || 0))}</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-700/40 bg-[#1A1A1A]">
          <div className="text-center">
            <p className="text-gray-500 text-xs font-medium">Showing top {Math.min(20, tokensWithRevenue.length)} tokens</p>
            <p className="text-gray-600 text-xs mt-1">Click any token for details</p>
          </div>
        </div>
      </div>
    </>
  );
} 